import { useEffect } from "react";
import { getAbsoluteUrl, getDefaultSocialImageUrl, getSiteOrigin } from "../site";

type StructuredData = Record<string, unknown> | Array<Record<string, unknown>>;

type SeoProps = {
  title: string;
  description: string;
  path?: string;
  type?: "website" | "article";
  keywords?: string[];
  noIndex?: boolean;
  publishedTime?: string;
  structuredData?: StructuredData;
  image?: string;
  imageAlt?: string;
};

function upsertMeta(attribute: "name" | "property", value: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${value}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

export function Seo({
  title,
  description,
  path,
  type = "website",
  keywords,
  noIndex = false,
  publishedTime,
  structuredData,
  image,
  imageAlt = "mutzur Website Vorschau",
}: SeoProps) {
  useEffect(() => {
    const origin = getSiteOrigin();
    const canonicalUrl = new URL(path ?? window.location.pathname, `${origin}/`).toString();
    const fullTitle = title.includes("mutzur") ? title : `${title} | mutzur`;
    const socialImageUrl = image ? getAbsoluteUrl(image) : getDefaultSocialImageUrl();

    document.documentElement.lang = "de";
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noIndex ? "noindex, nofollow" : "index, follow");
    upsertMeta("property", "og:locale", "de_DE");
    upsertMeta("property", "og:site_name", "mutzur");
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", socialImageUrl);
    upsertMeta("property", "og:image:alt", imageAlt);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", socialImageUrl);

    if (keywords?.length) {
      upsertMeta("name", "keywords", keywords.join(", "));
    } else {
      document.head.querySelector('meta[name="keywords"]')?.remove();
    }

    if (publishedTime) {
      upsertMeta("property", "article:published_time", publishedTime);
    } else {
      document.head.querySelector('meta[property="article:published_time"]')?.remove();
    }

    upsertLink("canonical", canonicalUrl);

    const structuredDataScriptId = "seo-structured-data";
    const existingScript = document.getElementById(structuredDataScriptId);

    if (structuredData) {
      const script =
        existingScript instanceof HTMLScriptElement ? existingScript : document.createElement("script");

      script.id = structuredDataScriptId;
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(structuredData);

      if (!existingScript) {
        document.head.appendChild(script);
      }
    } else {
      existingScript?.remove();
    }
  }, [description, image, imageAlt, keywords, noIndex, path, publishedTime, structuredData, title, type]);

  return null;
}
